<template>
  <div class="psychological-test-data-view">
    <div class="section-header">
      <h3>心理测评数据查询</h3>
      <div class="filter-controls">
        <el-select 
          v-model="selectedTest" 
          placeholder="选择心理测试" 
          clearable
          @change="handleTestChange"
          style="width: 200px; margin-right: 10px;"
        >
          <el-option
            v-for="test in tests"
            :key="test.test_id"
            :label="test.test_name"
            :value="test.test_id">
          </el-option>
        </el-select>
        <el-button type="primary" @click="loadData" :loading="loading">
          <i class="el-icon-search"></i> 查询
        </el-button>
      </div>
    </div>

    <div class="data-table-container">
      <el-table 
        :data="tableData" 
        v-loading="loading"
        stripe
        border
        style="width: 100%"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column prop="test_name" label="测试名称" width="200">
          <template slot-scope="scope">
            <el-tag type="info" size="small">{{ scope.row.test_name }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="score" label="测试分数" width="120" align="center">
          <template slot-scope="scope">
            <div class="score-display">
              <span class="score-value" :class="getScoreClass(scope.row.score)">
                {{ scope.row.score }}
              </span>
              <div class="score-level">
                <el-tag 
                  :type="getScoreTagType(scope.row.score)" 
                  size="mini"
                >
                  {{ getScoreLevel(scope.row.score) }}
                </el-tag>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="test_date" label="测试日期" width="120" align="center"></el-table-column>
        <el-table-column prop="created_at" label="记录时间" width="160" align="center"></el-table-column>
        <el-table-column prop="test_description" label="测试描述" min-width="200">
          <template slot-scope="scope">
            <span class="description-text">
              {{ scope.row.test_description || '无描述' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" align="center">
          <template slot-scope="scope">
            <el-button 
              type="text" 
              size="small" 
              @click="showDetail(scope.row)"
            >
              详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total">
        </el-pagination>
      </div>
    </div>

    <!-- 详情对话框 -->
    <el-dialog title="心理测评详情" :visible.sync="detailDialogVisible" width="600px">
      <div v-if="selectedRecord" class="detail-content">
        <div class="detail-item">
          <label>测试名称:</label>
          <span>{{ selectedRecord.test_name }}</span>
        </div>
        <div class="detail-item">
          <label>测试分数:</label>
          <div class="score-detail">
            <span class="score-highlight" :class="getScoreClass(selectedRecord.score)">
              {{ selectedRecord.score }}
            </span>
            <el-tag 
              :type="getScoreTagType(selectedRecord.score)" 
              size="medium"
              style="margin-left: 10px;"
            >
              {{ getScoreLevel(selectedRecord.score) }}
            </el-tag>
          </div>
        </div>
        <div class="detail-item">
          <label>测试日期:</label>
          <span>{{ selectedRecord.test_date }}</span>
        </div>
        <div class="detail-item">
          <label>记录时间:</label>
          <span>{{ selectedRecord.created_at }}</span>
        </div>
        <div class="detail-item" v-if="selectedRecord.test_description">
          <label>测试描述:</label>
          <p class="description-text">{{ selectedRecord.test_description }}</p>
        </div>
      </div>
    </el-dialog>

    <!-- 心理健康分析 -->
    <div class="analysis-container" v-if="tableData.length > 0">
      <h4>心理健康分析</h4>
      <div class="analysis-grid">
        <div class="analysis-card">
          <div class="card-header">
            <i class="el-icon-data-line"></i>
            <span>平均分数</span>
          </div>
          <div class="card-value">{{ averageScore.toFixed(1) }}</div>
          <div class="card-desc">{{ getScoreLevel(averageScore) }}水平</div>
        </div>
        <div class="analysis-card">
          <div class="card-header">
            <i class="el-icon-trophy"></i>
            <span>最高分数</span>
          </div>
          <div class="card-value">{{ maxScore }}</div>
          <div class="card-desc">{{ getScoreLevel(maxScore) }}水平</div>
        </div>
        <div class="analysis-card">
          <div class="card-header">
            <i class="el-icon-bottom"></i>
            <span>最低分数</span>
          </div>
          <div class="card-value">{{ minScore }}</div>
          <div class="card-desc">{{ getScoreLevel(minScore) }}水平</div>
        </div>
        <div class="analysis-card">
          <div class="card-header">
            <i class="el-icon-date"></i>
            <span>测试次数</span>
          </div>
          <div class="card-value">{{ tableData.length }}</div>
          <div class="card-desc">总测试次数</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getPsychologicalTestData, getPsychologicalTests } from '../../../api/dataQueryApi'

export default {
  name: 'PsychologicalTestDataView',
  data() {
    return {
      loading: false,
      tableData: [],
      tests: [],
      selectedTest: null,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      detailDialogVisible: false,
      selectedRecord: null
    }
  },
  computed: {
    averageScore() {
      if (this.tableData.length === 0) return 0
      const sum = this.tableData.reduce((acc, item) => acc + parseFloat(item.score), 0)
      return sum / this.tableData.length
    },
    maxScore() {
      if (this.tableData.length === 0) return 0
      return Math.max(...this.tableData.map(item => parseFloat(item.score)))
    },
    minScore() {
      if (this.tableData.length === 0) return 0
      return Math.min(...this.tableData.map(item => parseFloat(item.score)))
    }
  },
  mounted() {
    this.loadTests()
    this.loadData()
  },
  methods: {
    async loadTests() {
      try {
        const response = await getPsychologicalTests()
        this.tests = response.data
      } catch (error) {
        console.error('获取心理测试列表失败:', error)
      }
    },
    async loadData() {
      try {
        this.loading = true
        const params = {
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        }
        
        if (this.selectedTest) {
          params.test_id = this.selectedTest
        }
        
        const response = await getPsychologicalTestData(params)
        this.tableData = response.data.results
        this.pagination.total = response.data.total
      } catch (error) {
        console.error('获取心理测评数据失败:', error)
        this.$message.error(error.message || '获取心理测评数据失败')
      } finally {
        this.loading = false
      }
    },
    handleTestChange() {
      this.pagination.page = 1
      this.loadData()
    },
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.page = 1
      this.loadData()
    },
    handleCurrentChange(val) {
      this.pagination.page = val
      this.loadData()
    },
    showDetail(row) {
      this.selectedRecord = row
      this.detailDialogVisible = true
    },
    getScoreClass(score) {
      const numScore = parseFloat(score)
      if (numScore >= 80) {
        return 'score-excellent'
      } else if (numScore >= 70) {
        return 'score-good'
      } else if (numScore >= 60) {
        return 'score-normal'
      } else {
        return 'score-concern'
      }
    },
    getScoreTagType(score) {
      const numScore = parseFloat(score)
      if (numScore >= 80) {
        return 'success'
      } else if (numScore >= 70) {
        return 'primary'
      } else if (numScore >= 60) {
        return 'warning'
      } else {
        return 'danger'
      }
    },
    getScoreLevel(score) {
      const numScore = parseFloat(score)
      if (numScore >= 80) {
        return '优秀'
      } else if (numScore >= 70) {
        return '良好'
      } else if (numScore >= 60) {
        return '正常'
      } else {
        return '关注'
      }
    },
    getPsychologicalAssessment(score) {
      const numScore = parseFloat(score)
      if (numScore >= 80) {
        return '心理状态非常健康，适应能力强'
      } else if (numScore >= 70) {
        return '心理状态良好，基本适应正常'
      } else if (numScore >= 60) {
        return '心理状态正常，需要适当关注'
      } else {
        return '心理状态需要重点关注和干预'
      }
    },
    getPsychologicalSuggestion(score) {
      const numScore = parseFloat(score)
      if (numScore >= 80) {
        return '继续保持良好的心理状态，可以适当分享经验帮助他人'
      } else if (numScore >= 70) {
        return '保持现有的心理调节方式，适当增加放松活动'
      } else if (numScore >= 60) {
        return '建议增加心理调节活动，如运动、音乐、社交等'
      } else {
        return '建议寻求专业心理咨询师的帮助和指导'
      }
    }
  }
}
</script>

<style scoped>
.psychological-test-data-view {
  padding: 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
}

.section-header h3 {
  color: #333;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.filter-controls {
  display: flex;
  align-items: center;
}

.data-table-container {
  background: white;
}

.score-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.score-value {
  font-weight: 600;
  font-size: 16px;
}

.score-excellent {
  color: #67c23a;
}

.score-good {
  color: #409eff;
}

.score-normal {
  color: #e6a23c;
}

.score-concern {
  color: #f56c6c;
}

.description-text {
  color: #666;
  line-height: 1.4;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.detail-content {
  padding: 10px 0;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item label {
  width: 120px;
  color: #666;
  font-weight: 500;
  flex-shrink: 0;
}

.detail-item span {
  color: #333;
  flex: 1;
}

.score-detail {
  display: flex;
  align-items: center;
}

.score-highlight {
  font-size: 18px;
  font-weight: 600;
}

.psychological-assessment {
  flex: 1;
}

.assessment-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.assessment-label {
  width: 80px;
  color: #666;
  font-size: 14px;
}

.assessment-优秀 {
  color: #67c23a;
  font-weight: 500;
}

.assessment-良好 {
  color: #409eff;
  font-weight: 500;
}

.assessment-正常 {
  color: #e6a23c;
  font-weight: 500;
}

.assessment-关注 {
  color: #f56c6c;
  font-weight: 500;
}

.assessment-suggestion {
  color: #666;
  font-size: 14px;
  line-height: 1.4;
}

.analysis-container {
  margin-top: 30px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.analysis-container h4 {
  color: #333;
  margin-bottom: 20px;
  font-size: 16px;
  font-weight: 600;
}

.analysis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.analysis-card {
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  text-align: center;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
  margin-bottom: 10px;
}

.card-value {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
}

.card-desc {
  color: #999;
  font-size: 12px;
}

@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .filter-controls {
    justify-content: center;
  }
  
  .detail-item {
    flex-direction: column;
    gap: 5px;
  }
  
  .detail-item label {
    width: auto;
  }
  
  .analysis-grid {
    grid-template-columns: 1fr;
  }
}
</style>
